import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import IconButton from "@mui/material/IconButton";
import { Button, Stack, Tooltip, useTheme } from "@mui/material";
import { AddRounded, DeleteRounded, WarningRounded } from "@mui/icons-material";
import { STATE_STORES_ACTIVE_FILTERS, STATE_STORES_TABLE_UPDATING } from "../constants/ClustersRecoil";
import InfoRoundedIcon from "@mui/icons-material/Info";
import FinanceDialog from "../../../component/FinanceDialog";
import { useClusterStoresRepository } from "../../../api/repositories/ClusterStoresRepository";
import StoreCreateModal from "../modals/StoreCreateModal";
import { useParams } from "react-router-dom";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import { useHasScope } from "../../../../../config/globalConfig";
import { ADD_MS_TO_CLUSTER, DELETE_CLUSTER } from "../../../utils/FinanceScope";

const StoresTable = ({legalEntity, dealer}: {legalEntity: string, dealer: string}) => {

    const UNIQUE_ID = "fnn-clusters-stores";
    const hasScope = useHasScope;
    const { t } = useTranslation("finance", { keyPrefix: "clusters" });
    const { getStores, deleteStore } = useClusterStoresRepository();
    const { id } = useParams();

    const filterDefs = useRecoilValue(STATE_STORES_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_STORES_TABLE_UPDATING);

    const gridRef = useRef<AgGridReact>(null);
    const theme = useTheme();

    const [warningOpen, setWarningOpen] = useState(false);

    const [openCreateModal, setOpenCreateModal] = useState(false);
    // const [openEditModal, setOpenEditModal] = useState(false);
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [message, setMessage] = useState('');

    // const [store, setStore] = useState<any>();
    const [storeToDelete, setStoreToDelete] = useState<any>({});

    const columnDefs: ColDef[] = [
        {
            field: "identificationCode", headerName: t("tables.store.identification-code"),
            minWidth: 150,
            unSortIcon: true, sortable: true
        },
        {
            field: "code", headerName: t("tables.store.code"),
            minWidth: 150,
            unSortIcon: true, sortable: true
        },
        {
            field: "name", headerName: t("tables.store.name"),
            minWidth: 150,
            unSortIcon: true, sortable: true,
        },
        {
            field: "_actions", headerName: '',
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => (
                            <FinanceDynamicActions>
          
                                {hasScope(DELETE_CLUSTER) &&
                                        <Tooltip arrow title={t('tables.store.actions.tooltips.delete')}>
                                            <IconButton
                                                    sx={{ color: theme.palette.accent.main }}
                                                    onClick={() => {
                                                        setStoreToDelete({ id: params.data.id, name: params.data.name });
                                                        setWarningOpen(true);
                                                    }}
                                            >
                                                <DeleteRounded/>
                                            </IconButton>
                                        </Tooltip>
                                }
                            </FinanceDynamicActions>
                    ),
                };
            },
            suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: { title: t('tables.store.no-rows-overlay.title') }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
        }
    }, [updating]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].colId;
                let order =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].sort;

                getStores(id!, startRow, endRow - startRow, sort, order, filterDefs)
                        .then((response) => {
                            let result = response.result;

                            if (result.page.totalElements === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result.page.totalElements,
                            });
                        })
                        .catch(() => params.fail());
            },
        };
    };

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (
                <></>
        ),
        renderActions: () => (
                <Stack>
                    {hasScope(ADD_MS_TO_CLUSTER) &&
                            <Button fullWidth color={"accent"} variant="contained" component="label"
                                    startIcon={<AddRounded/>}
                                    onClick={() => {
                                        setOpenCreateModal(true);
                                    }}>
                                {t('preview.buttons.add-store')}
                            </Button>
                    }
                </Stack>
        )
    }

    return (
            <>
                <AgGridCustomTable
                        toolbar={toolbar}
                        rowModelType="serverSide"
                        pagination paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                        gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                        rowSelection={"multiple"}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        suppressRowClickSelection
                        onGridReady={() => {
                            gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                            let gridState = getLocalGridState(UNIQUE_ID).cols;
                            if (gridState !== undefined) {
                                gridRef.current!.columnApi.applyColumnState({
                                    state: gridState,
                                    applyOrder: true
                                })
                            }
                        }}
                        onColumnMoved={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnPinned={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnResized={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onSortChanged={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onPaginationChanged={() => {
                            setLocalGridState(UNIQUE_ID, {
                                rows: gridRef.current!.api?.paginationGetPageSize(),
                                cols: getLocalGridState(UNIQUE_ID).cols
                            });
                        }}
                ></AgGridCustomTable>
                <FinanceDialog open={warningOpen}
                               onClose={() => setWarningOpen(false)}
                               callback={() => {
                                   deleteStore(storeToDelete.id!).then((res) => {
                                       setUpdating(true)
                                   });
                               }}
                               titleIcon={<InfoRoundedIcon sx={{ color: 'accent.main' }}/>}
                               title={t('store-delete-confirmation.title')}
                               content={t('store-delete-confirmation.text', { name: storeToDelete.name })}
                               confirmButton={t('store-delete-confirmation.confirm')}
                               cancelButton={t('store-delete-confirmation.cancel')}
                />
                <FinanceDialog
                        title={t('error-dialog.errors.cluster-association.title')}
                        content={message}
                        cancelButton={t('error-dialog.close')}
                        open={openErrorDialog}
                        onClose={() => {
                            setOpenErrorDialog(false)
                        }}
                        titleIcon={<WarningRounded sx={{ color: 'error.main' }}/>}
                />
                <StoreCreateModal open={openCreateModal} onClose={() => {
                    setOpenCreateModal(false)
                }} clusterId={parseInt(id!)} setOpenErrorDialog={setOpenErrorDialog} setMessage={setMessage} legalEntity={legalEntity} dealer={dealer}/>
            </>
    );
};

export default StoresTable;